import {Component, DestroyRef, ViewChild, inject} from '@angular/core';
import {MatCard, MatCardContent} from '@angular/material/card';
import {MatButton} from '@angular/material/button';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {ActivatedRoute} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslocoModule} from '@jsverse/transloco';
import {CodeEntryComponent} from '../../../../../shared/ui/code-entry/code-entry.component';
import {DeviceService} from './device.service';

// A user_code is 8 characters shown as XXXX-XXXX (RFC 8628 §6.1, ambiguity-free
// uppercase set). The enter-code screen renders one input cell per character via
// the shared CodeEntryComponent (Story 47.1 extracted it from here so the admin
// console's 6-digit MFA screens share the paste-fills-all logic); input is forced
// uppercase and the backend re-validates against its charset.
const CODE_LENGTH = 8;
const CODE_GROUP = CODE_LENGTH / 2; // the hyphen sits between the two 4-char groups

// The verification page's finite states. There is NO explicit approve/deny step:
// RFC 8628 §3.3 mandates only a user-interaction *sequence* (the sign-in), not an
// explicit approval, and §5.4's confirm step is RECOMMENDED, not required. Once the
// user is signed in we authorize automatically ('connecting') and show a receipt
// ('approved'); the device learns the outcome by polling /oauth/token (32.3).
// 'access-denied' is distinct from 'error' so a missing grant (403) reads clearly
// instead of masquerading as an expired code.
type DeviceState =
  | 'enter-code'
  | 'sign-in-required'
  | 'loading'
  | 'connecting'
  | 'approved'
  | 'access-denied'
  | 'error';

@Component({
  selector: 'app-device',
  standalone: true,
  imports: [
    MatCard, MatCardContent,
    MatButton,
    MatProgressSpinnerModule,
    TranslocoModule,
    CodeEntryComponent,
  ],
  templateUrl: './device.component.html',
  styleUrl: './device.component.css',
})
export class DeviceComponent {
  state: DeviceState = 'enter-code';
  appName = '';
  scopes: string[] = [];
  submitting = false;

  /** The URL-carried user_code (if any), handed to the code-entry component as
   *  its initial value. Kept as a field because the component only exists once
   *  the enter-code state renders, while the query-param subscription runs in
   *  the constructor. */
  prefill = '';
  readonly codeLength = CODE_LENGTH;
  readonly codeGroup = CODE_GROUP;

  @ViewChild(CodeEntryComponent) private entry?: CodeEntryComponent;

  private userCode = '';

  private readonly service = inject(DeviceService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // Story 53.9 — the session is the host-only, HttpOnly cookie the browser
    // attaches to the lookup; the page never holds it. "Signed in?" is answered
    // by the lookup itself: a 401 drives the sign-in round-trip (doLookup).
    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe(params => {
      const raw = (params['user_code'] ?? '').trim();
      if (!raw) {
        // No pre-filled code (the user typed the URL by hand) — let them enter it.
        return;
      }
      this.prefill = raw;
      // Only auto-proceed when the URL carried a full code (the QR /
      // verification_uri_complete always does); a partial value is left for the
      // user to finish in the enter-code state (pre-filled via `prefill`).
      const normalized = normalizeUserCode(raw);
      if (normalized.length !== CODE_LENGTH) {
        return;
      }
      this.userCode = hyphenate(normalized);
      this.doLookup(this.userCode);
    });
  }

  // --- code-cell entry (delegated to the shared CodeEntryComponent) ----------

  /** True once every cell holds a character. */
  codeComplete(): boolean {
    return this.entry?.complete() ?? false;
  }

  /** The eight cells joined as XXXX-XXXX. */
  private assembledCode(): string {
    return hyphenate(this.entry?.value() ?? '');
  }

  // The enter-code form is a bare <form> with no [formGroup]/NgForm, so Angular's
  // (ngSubmit) never fires here. Bind the NATIVE submit event and preventDefault
  // ourselves; without this the type="submit" button did a full-page GET reload
  // back to /device, dropping the entered code ("leads back to the same page").
  onSubmit(event: Event): void {
    event.preventDefault();
    this.submitCode();
  }

  submitCode(): void {
    if (!this.codeComplete() || this.submitting) {
      return;
    }
    this.userCode = this.assembledCode();
    this.doLookup(this.userCode);
  }

  // --- lookup + automatic authorization -------------------------------------

  private doLookup(code: string): void {
    this.state = 'loading';
    this.submitting = true;
    this.service.lookup(code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resp => {
          this.appName = resp.app_name;
          this.scopes = resp.scopes ?? [];
          // No approve button — authorize immediately (see DeviceState docs). We
          // stay "submitting" straight through the approve so the two calls read
          // as one uninterrupted action.
          this.autoApprove(code);
        },
        error: (err: HttpErrorResponse) => {
          this.submitting = false;
          // Expired/absent session mid-flow → drive the sign-in round-trip rather
          // than dead-ending on an error. Any other error → generic.
          if (err.status === 401) {
            this.gotoSignIn(code);
          } else {
            this.state = 'error';
          }
        },
      });
  }

  private autoApprove(code: string): void {
    this.state = 'connecting';
    this.service.approve(code)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resp => {
          this.submitting = false;
          this.state = resp.result === 'approved' ? 'approved' : 'error';
        },
        error: (err: HttpErrorResponse) => this.handleActionError(err),
      });
  }

  private handleActionError(err: HttpErrorResponse): void {
    this.submitting = false;
    if (err.status === 401) {
      // Session expired between lookup and approve → re-run the sign-in round-trip.
      this.gotoSignIn(this.userCode);
    } else if (err.status === 403 && (err.error as {error?: string} | null)?.error === 'mfa_required') {
      // Story 47.2 AC5: the application requires MFA and this session has not
      // done it. Hop to the challenge page; it resumes to /device?user_code=…
      // once the session is elevated. The body names the client because a
      // device page has no client_id in its URL and /mfa/* needs one.
      this.state = 'sign-in-required';
      this.redirectToMfaChallenge(this.userCode, (err.error as {client_id?: string}).client_id ?? '');
    } else if (err.status === 403) {
      // The signed-in account has no grant for this client (deviceMapAuthzError →
      // access_denied). Show this distinctly, not as "invalid or expired".
      this.state = 'access-denied';
    } else {
      this.state = 'error';
    }
  }

  private gotoSignIn(code: string): void {
    this.state = 'sign-in-required';
    this.redirectToLogin(code);
  }

  // Extracted for testability — Karma spies on this to assert the redirect fires
  // without actually navigating. Reuses the existing login page made return-aware:
  // after auth it navigates back to /device?user_code=… (Story 32.4 AC2); the
  // session cookie /login sets goes with it.
  protected redirectToLogin(code: string): void {
    window.location.href = `/login?device_user_code=${encodeURIComponent(code)}`;
  }

  // Device-mode step-up (Story 47.2): the challenge page reads device_user_code
  // and client_id from the query string and resumes to /device. Story 53.9: no
  // session in the URL — the challenge page's calls carry the cookie.
  protected redirectToMfaChallenge(code: string, clientId: string): void {
    const params = new URLSearchParams({device_user_code: code, client_id: clientId});
    window.location.href = `/mfa/challenge?${params.toString()}`;
  }
}

/** Uppercase, separators stripped — the canonical user_code character set. */
function normalizeUserCode(raw: string): string {
  return raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** XXXXXXXX → XXXX-XXXX (the wire format /device/lookup + /device/approve expect). */
function hyphenate(chars: string): string {
  return chars.slice(0, CODE_GROUP) + '-' + chars.slice(CODE_GROUP);
}
